import { Button } from "@community-project-org/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { JSX } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { apiRequest, ApiRequestError } from "../../common/api/apiClient.js";
import { formatWhen } from "../../common/components/formatWhen.js";
import { Money } from "../../common/components/Money.js";
import { Stepper } from "../../common/components/Stepper.js";
import type { components } from "../../generated/api.js";

type ServiceDTO = components["schemas"]["ServiceDTO"];
type RouteDTO = components["schemas"]["RouteDTO"];
type StopDTO = components["schemas"]["StopDTO"];
type RouteQuoteDTO = components["schemas"]["RouteQuoteDTO"];
type BookingDTO = components["schemas"]["BookingDTO"];

const MAX_PER_BOOKING = 10;
const selectClass = "min-h-[44px] w-full rounded-md border border-line bg-surface px-2 text-[14px] text-ink";

function stopLabel(stop: StopDTO): string {
  const time = stop.departsAt ?? stop.arrivesAt;
  return time ? `${stop.name} · ${formatWhen(time, stop.timeZone)}` : stop.name;
}

function position(value: string | null, fallback: number): number {
  const parsed = value === null ? Number.NaN : Number(value);
  return Number.isInteger(parsed) ? parsed : fallback;
}

/**
 * Buchung einer Fahrt: Ein- und Ausstieg, Anzahl. Preis und freie Plätze gelten für
 * genau diese Teilstrecke und kommen vom Server; aus der Suche sind beide Halte schon
 * gesetzt.
 */
export function RouteBooking({ offer }: { offer: ServiceDTO }): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const [from, setFrom] = useState(() => position(params.get("von"), 0));
  const [to, setTo] = useState(() => position(params.get("nach"), offer.stopCount - 1));
  const [quantity, setQuantity] = useState(1);
  const base = `/offers/${encodeURIComponent(offer.id)}/route`;

  const route = useQuery({
    queryKey: ["offer-route", offer.id],
    queryFn: () => apiRequest<RouteDTO>(base, { tenant: null }),
  });
  const quote = useQuery({
    queryKey: ["offer-route-quote", offer.id, from, to],
    enabled: to > from,
    retry: false,
    queryFn: () => apiRequest<RouteQuoteDTO>(`${base}/quote?from=${from}&to=${to}`, { tenant: null }),
  });

  const book = useMutation({
    mutationFn: () =>
      apiRequest<BookingDTO>("/bookings", {
        method: "POST",
        body: { serviceId: offer.id, fromStop: from, toStop: to, quantity },
        tenant: null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
      navigate("/tickets");
    },
    onError: async (error) => {
      if (error instanceof ApiRequestError && error.status === 409) {
        await quote.refetch();
      }
    },
  });

  if (route.isPending) {
    return <p className="text-[14px] text-ink-3">Wird geladen …</p>;
  }
  if (route.isError) {
    return <p className="text-[14px] text-danger">Diese Fahrt ist gerade nicht erreichbar.</p>;
  }
  const stops = route.data.stops;
  const free = quote.data?.free ?? null;
  const max = Math.min(MAX_PER_BOOKING, free ?? MAX_PER_BOOKING);

  return (
    <div className="grid gap-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="grid gap-1 text-[12px] font-bold text-ink-2">
          Einstieg
          <select
            className={selectClass}
            value={from}
            onChange={(event) => {
              const next = Number(event.target.value);
              setFrom(next);
              if (to <= next) {
                setTo(next + 1);
              }
            }}
          >
            {stops.slice(0, -1).map((stop) => (
              <option key={stop.position} value={stop.position}>
                {stopLabel(stop)}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-[12px] font-bold text-ink-2">
          Ausstieg
          <select className={selectClass} value={to} onChange={(event) => setTo(Number(event.target.value))}>
            {stops.filter((stop) => stop.position > from).map((stop) => (
              <option key={stop.position} value={stop.position}>
                {stopLabel(stop)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="num text-[13px] font-bold text-ink-2">
          {free === null ? "" : free === 0 ? "Auf dieser Strecke ausgebucht" : `${free} frei auf dieser Strecke`}
        </span>
        <Stepper label="Personen" value={Math.min(quantity, Math.max(1, max))} min={1} max={Math.max(1, max)} onChange={setQuantity} />
      </div>

      {book.error && (
        <p className="text-[13px] text-danger" role="alert">
          {book.error instanceof ApiRequestError ? book.error.message : "Das hat nicht geklappt."}
        </p>
      )}

      <div className="flex items-center justify-between gap-3">
        <span className="text-[17px] font-extrabold text-ink">
          {quote.data && <Money cents={quote.data.amountCents * quantity} currency={quote.data.currency} />}
        </span>
        <Button
          disabled={!quote.data || free === 0 || book.isPending}
          aria-busy={book.isPending}
          onClick={() => book.mutate()}
        >
          {book.isPending ? "Wird gebucht …" : "Buchen"}
        </Button>
      </div>
    </div>
  );
}
